"use client";

import { useEffect } from "react";
import type { Metric } from "web-vitals";
import { sendOwnedAnalyticsEvent } from "@/lib/analytics-server";
import {
  isBrowserThirdPartyTelemetrySuppressed,
  privacySafeTelemetryPath,
  privacySafeTelemetryUrl,
} from "@/lib/telemetry-route-policy";

type CoreMetricName = "LCP" | "INP" | "CLS";

const CORE_WEB_VITAL_THRESHOLDS: Record<
  CoreMetricName,
  { good: number; poor: number; unit: "ms" | "score" }
> = {
  LCP: { good: 2500, poor: 4000, unit: "ms" },
  INP: { good: 200, poor: 500, unit: "ms" },
  CLS: { good: 0.1, poor: 0.25, unit: "score" },
};

function isCoreMetric(name: string): name is CoreMetricName {
  return name === "LCP" || name === "INP" || name === "CLS";
}

function roundedMetricValue(metric: Metric) {
  return Math.round(metric.name === "CLS" ? metric.value * 1000 : metric.value);
}

function roundedMetricDelta(metric: Metric) {
  return Math.round(metric.name === "CLS" ? metric.delta * 1000 : metric.delta);
}

function getConnectionContext() {
  const connection =
    typeof navigator !== "undefined"
      ? (
          navigator as Navigator & {
            connection?: {
              effectiveType?: string;
              saveData?: boolean;
              downlink?: number;
              rtt?: number;
            };
          }
        ).connection
      : undefined;

  return {
    connection_effective_type: connection?.effectiveType,
    connection_save_data: connection?.saveData,
    connection_downlink: connection?.downlink,
    connection_rtt: connection?.rtt,
  };
}

function getPageContext() {
  if (typeof window === "undefined") {
    return {};
  }

  return {
    page_path: privacySafeTelemetryPath(window.location.pathname),
    page_url: privacySafeTelemetryUrl(window.location.href),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_class: window.innerWidth < 768 ? "mobile" : "desktop",
    visibility_state: document.visibilityState,
    ...getConnectionContext(),
  };
}

function getReleaseContext() {
  // Stamp the build that generated the interaction. Rolling RUM windows often
  // cross deployments; without this dimension a fixed pre-release sample is
  // indistinguishable from a regression on the current release.
  const release = process.env.NEXT_PUBLIC_SENTRY_RELEASE?.trim();
  return release && /^[0-9a-f]{40}$/i.test(release) ? { release } : {};
}

// web-vitals/attribution debug payloads. Each metric carries its OWN attribution
// shape (CLSAttribution / INPAttribution / LCPAttribution); we read the one that
// matches and emit it on breaches, so a field-only metric the lab can't reproduce
// names its culprit (element + timing sub-phase) in BigQuery. Every key stays
// <=40 chars and values are clamped — the owned beacon sink drops longer keys and
// truncates string values to 256 chars (see lib/beacon-bigquery.ts).
function attributionOf(metric: Metric): Record<string, unknown> | undefined {
  return (metric as Metric & { attribution?: Record<string, unknown> }).attribution;
}

function truncateSelector(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;
  return value.slice(0, 256);
}

function roundMs(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? Math.round(value) : undefined;
}

function scriptSourcePath(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;
  try {
    const source = new URL(value, window.location.origin);
    const firstParty =
      source.origin === window.location.origin ||
      source.hostname === "ishvaram.com" ||
      source.hostname === "www.ishvaram.com";
    const host = firstParty ? "" : source.hostname;
    return `${host}${source.pathname}`.slice(0, 256);
  } catch {
    return value.slice(0, 256);
  }
}

// CLS: the largest layout shift's element + when/how much it shifted.
function clsAttribution(metric: Metric) {
  if (metric.name !== "CLS") return {};
  const attribution = attributionOf(metric);
  if (!attribution) return {};
  return {
    cls_largest_shift_target: truncateSelector(attribution.largestShiftTarget),
    cls_largest_shift_value: attribution.largestShiftValue,
    cls_largest_shift_time: roundMs(attribution.largestShiftTime),
    cls_load_state: attribution.loadState,
  };
}

// INP: the interaction's element + interaction-phase breakdown (input vs
// processing vs presentation) + the longest blocking script. This is the modern
// field-INP debug payload — INP is the #1 mobile bottleneck and is frequently
// NOT lab-reproducible, so the element selector + sub-phase timings are the only
// way to root-cause a slow interaction from the field.
function inpAttribution(metric: Metric) {
  if (metric.name !== "INP") return {};
  const attribution = attributionOf(metric);
  if (!attribution) return {};
  const longestScript = attribution.longestScript as
    | {
        intersectingDuration?: unknown;
        subpart?: unknown;
        entry?: { sourceURL?: unknown; invokerType?: unknown };
      }
    | undefined;
  return {
    inp_interaction_target: truncateSelector(attribution.interactionTarget),
    inp_interaction_type: attribution.interactionType,
    inp_input_delay: roundMs(attribution.inputDelay),
    inp_processing_duration: roundMs(attribution.processingDuration),
    inp_presentation_delay: roundMs(attribution.presentationDelay),
    inp_interaction_time: roundMs(attribution.interactionTime),
    inp_load_state: attribution.loadState,
    inp_longest_script_duration: roundMs(longestScript?.intersectingDuration),
    inp_longest_script_subpart: longestScript?.subpart,
    inp_longest_script_source: scriptSourcePath(longestScript?.entry?.sourceURL),
    inp_longest_script_invoker: longestScript?.entry?.invokerType,
    inp_total_script_duration: roundMs(attribution.totalScriptDuration),
    inp_total_style_layout_duration: roundMs(attribution.totalStyleAndLayoutDuration),
    inp_total_paint_duration: roundMs(attribution.totalPaintDuration),
    inp_total_unattributed_duration: roundMs(attribution.totalUnattributedDuration),
  };
}

// LCP: the largest element + the 4 standard LCP sub-parts (TTFB → resource load
// delay → resource load duration → element render delay), so a slow LCP names
// whether to fix the server, image loading, or render-blocking.
function lcpAttribution(metric: Metric) {
  if (metric.name !== "LCP") return {};
  const attribution = attributionOf(metric);
  if (!attribution) return {};
  return {
    lcp_target: truncateSelector(attribution.target),
    lcp_url: truncateSelector(attribution.url),
    lcp_time_to_first_byte: roundMs(attribution.timeToFirstByte),
    lcp_resource_load_delay: roundMs(attribution.resourceLoadDelay),
    lcp_resource_load_duration: roundMs(attribution.resourceLoadDuration),
    lcp_element_render_delay: roundMs(attribution.elementRenderDelay),
  };
}

export function metricPayload(metric: Metric) {
  const threshold = isCoreMetric(metric.name) ? CORE_WEB_VITAL_THRESHOLDS[metric.name] : undefined;

  return {
    metric_name: metric.name,
    metric_key: metric.name.toLowerCase(),
    error_key: metric.name,
    error_kind: "web_vitals",
    metric_value: roundedMetricValue(metric),
    metric_value_raw: metric.value,
    metric_delta: roundedMetricDelta(metric),
    metric_delta_raw: metric.delta,
    metric_rating: metric.rating,
    metric_id: metric.id,
    metric_navigation_type: metric.navigationType,
    metric_threshold_good: threshold?.good,
    metric_threshold_poor: threshold?.poor,
    metric_unit: threshold?.unit,
    ...clsAttribution(metric),
    ...inpAttribution(metric),
    ...lcpAttribution(metric),
    ...getPageContext(),
    ...getReleaseContext(),
  };
}

function sendToGTM(metric: Metric) {
  if (
    typeof window === "undefined" ||
    !window.dataLayer ||
    isBrowserThirdPartyTelemetrySuppressed()
  )
    return;
  window.dataLayer.push({
    event: "web_vitals",
    ...metricPayload(metric),
  });
}

function sendThresholdBreach(metric: Metric) {
  if (!isCoreMetric(metric.name) || metric.rating === "good") return;

  const payload = {
    ...metricPayload(metric),
    breach_severity: metric.rating,
  };

  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: "web_vitals_threshold_breach",
      ...payload,
    });
  }

  sendOwnedAnalyticsEvent("web_vitals_threshold_breach", payload);
}

function report(metric: Metric) {
  // GTM dataLayer push keeps GA4 web-vitals dashboards working.
  // Threshold breaches additionally go to the first-party BigQuery beacon.
  sendToGTM(metric);
  sendThresholdBreach(metric);
}

export default function WebVitalsReporter() {
  useEffect(() => {
    // Attribution build: populates metric.attribution so each breach carries its
    // metric-specific debug payload (CLS shift target, INP interaction target +
    // timing sub-phases, LCP element + sub-parts) — the ONLY way to diagnose a
    // field-only metric the lab can't reproduce (see the *Attribution emitters).
    import("web-vitals/attribution").then(({ onCLS, onINP, onLCP, onFCP, onTTFB }) => {
      onCLS(report);
      onINP(report);
      onLCP(report);
      onFCP(report);
      onTTFB(report);
    });
  }, []);

  return null;
}
